import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiUpload,
  FiX,
} from "react-icons/fi";
import API from "../utils/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import "./BusinessRegisterPage.css";

import DocUploadField from "../components/common/DocUploadField";
const COMPANY_TYPES = [
  { value: "sole_proprietorship", label: "Sole Proprietorship" },
  { value: "partnership", label: "Partnership" },
  { value: "private_limited", label: "Private Limited (Pvt. Ltd.)" },
  { value: "public_limited", label: "Public Limited" },
  { value: "llp", label: "Limited Liability Partnership (LLP)" },
  { value: "ngo", label: "NGO / Trust" },
  { value: "other", label: "Other" },
];

const INDUSTRIES = [
  "Advertising & Marketing",
  "Architecture & Design",
  "Education",
  "Events & Entertainment",
  "Fashion & Apparel",
  "Food & Beverage",
  "Healthcare",
  "Hospitality & Tourism",
  "IT & Technology",
  "Manufacturing",
  "Media & Publishing",
  "Real Estate",
  "Retail & E-commerce",
  "Sports & Fitness",
  "Other",
];

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Delhi",
  "Chandigarh",
  "Puducherry",
  "Jammu & Kashmir",
  "Ladakh",
];
// ─── File Upload Sub-component ────────────────────────────────────────────────
const FileUploadField = ({ label, optional, fieldName, value, onChange }) => {
  const inputRef = useRef();
  const isImage = value?.url && /\.(jpg|jpeg|png|svg)$/i.test(value.url);
  const [dragging, setDragging] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/svg+xml",
    ];
    if (!allowed.includes(file.type)) {
      toast.error("Only PDF, PNG, JPEG, or SVG allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be under 5MB");
      return;
    }
    const formData = new FormData();
    formData.append("image", file);
    try {
      const res = await API.post("/upload/public", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        onChange(fieldName, { url: res.data.url, name: file.name });
        toast.success("File uploaded");
      }
    } catch {
      toast.error("Upload failed. Try again.");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    handleFile({ target: { files: [file] } });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };
  const handleDragLeave = () => setDragging(false);

  return (
    <div className="file-upload-field">
      <label className="file-upload-label">
        {label}
        {optional && <span className="optional"> (optional)</span>}
      </label>
      {value?.url ? (
        <div className="file-preview">
          {isImage ? (
            <img src={value.url} alt={label} className="file-preview-img" />
          ) : (
            <div className="file-preview-doc">
              <span className="file-icon">📄</span>
              <span className="file-name">{value.name || "Document"}</span>
            </div>
          )}
          <button
            type="button"
            className="file-remove-btn"
            onClick={() => onChange(fieldName, null)}
            title="Remove file"
          >
            <FiX size={14} />
          </button>
        </div>
      ) : (
        <div
          className={`file-upload-zone ${dragging ? "dragging" : ""}`}
          onClick={() => inputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <FiUpload size={20} />
          <span>Click or drag & drop PNG, JPEG, or SVG</span>
          <span className="file-hint">Max 5MB</span>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.svg"
        style={{ display: "none" }}
        onChange={handleFile}
      />
    </div>
  );
};
// ─── Main Component ───────────────────────────────────────────────────────────
const BusinessRegisterPage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sameAsReg, setSameAsReg] = useState(false);
  const [agreeCompliance, setAgreeCompliance] = useState(true);
  const [agreeDeclaration, setAgreeDeclaration] = useState(true);
  const logoInputRef = useRef();
  const [form, setForm] = useState({
    // Step 1 – Company Info
    companyName: "",
    companyType: "",
    industry: "",
    website: "",
    logoFile: null,

    // Step 2 – Company Documents
    gstNumber: "",
    gstFile: null,
    panNumber: "",
    panFile: null,
    tanNumber: "",
    tanFile: null,
    udyamNumber: "",
    udyamFile: null,

    // Step 3 – Contact Person
    contactPersonName: user?.name || "",
    contactPersonDesignation: "",
    contactPersonPhone: user?.phone || "",
    contactPersonEmail: user?.email || "",

    // Step 4 – Address (Registered)
    regAddressLine1: "",
    regAddressLine2: "",
    regCity: "",
    regState: "",
    regPincode: "",
    regCountry: "India",
    // Address (Communication)
    commAddressLine1: "",
    commAddressLine2: "",
    commCity: "",
    commState: "",
    commPincode: "",
    commCountry: "India",

    // Step 5 – Bank Details
    bankName: "",
    accountNumber: "",
    branchName: "",
    ifscCode: "",
  });

  const handleChange = (e) => {
  const { name, value } = e.target;
  setForm((f) => {
    const updated = { ...f, [name]: value };
    if (name === "gstNumber") {
      const g = value.trim().toUpperCase();
      updated.panNumber = g.length >= 12 ? g.slice(2, 12) : "";
    }
    return updated;
  });
};
  const handleFileChange = (fieldName, value) =>
    setForm((f) => ({ ...f, [fieldName]: value }));

  // Auto-fill comm address from reg address
  const handleSameAsReg = (e) => {
    const checked = e.target.checked;
    setSameAsReg(checked);
    if (checked) {
      setForm((f) => ({
        ...f,
        commAddressLine1: f.regAddressLine1,
        commAddressLine2: f.regAddressLine2,
        commCity: f.regCity,
        commState: f.regState,
        commPincode: f.regPincode,
        commCountry: f.regCountry,
      }));
    }
  };

  // ── Validators ──────────────────────────────────────────────────────────────
  const validateStep1 = () => {
    if (!form.companyName) {
      toast.error("Company name is required");
      return false;
    }
    if (!form.companyType) {
      toast.error("Company type is required");
      return false;
    }
    if (!form.industry) {
      toast.error("Industry is required");
      return false;
    }
    return true;
  };

  const validateStep2 = () => true; // All optional

  const validateStep3 = () => {
    if (!form.contactPersonName) {
      toast.error("Contact person name is required");
      return false;
    }
    if (!form.contactPersonPhone) {
      toast.error("Contact phone is required");
      return false;
    }
    if (!/^[6-9]\d{9}$/.test(form.contactPersonPhone)) {
      toast.error("Enter valid 10-digit phone number");
      return false;
    }
    if (!form.contactPersonEmail) {
      toast.error("Contact email is required");
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!form.regAddressLine1) {
      toast.error("Registered address is required");
      return false;
    }
    if (!form.regCity) {
      toast.error("Registered city is required");
      return false;
    }
    if (!form.regState) {
      toast.error("Registered state is required");
      return false;
    }
    if (!form.regPincode) {
      toast.error("Registered pincode is required");
      return false;
    }
    if (!/^\d{6}$/.test(form.regPincode)) {
      toast.error("Enter valid 6-digit pincode");
      return false;
    }
    return true;
  };

  const validateStep5 = () => true;

  const TOTAL_STEPS = 6;

  const handleNext = () => {
    if (step === 1 && !validateStep1()) return;
    if (step === 2 && !validateStep2()) return;
    if (step === 3 && !validateStep3()) return;
    if (step === 4 && !validateStep4()) return;
    if (step === 5 && !validateStep5()) return;
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        companyName: form.companyName,
        companyType: form.companyType,
        industry: form.industry,
        website: form.website || null,
        logoUrl: form.logoFile?.url || null,
        // Documents
        gstNumber: form.gstNumber || null,
        gstDocUrl: form.gstFile?.url || null,
        panNumber: form.panNumber || null,
        panDocUrl: form.panFile?.url || null,
        tanNumber: form.tanNumber || null,
        tanDocUrl: form.tanFile?.url || null,
        udyamNumber: form.udyamNumber || null,
        udyamDocUrl: form.udyamFile?.url || null,
        // Contact
        contactPersonName: form.contactPersonName,
        contactPersonDesignation: form.contactPersonDesignation || null,
        contactPersonPhone: form.contactPersonPhone,
        contactPersonEmail: form.contactPersonEmail,
        // Registered Address
        regAddressLine1: form.regAddressLine1,
        regAddressLine2: form.regAddressLine2 || null,
        regCity: form.regCity,
        regState: form.regState,
        regPincode: form.regPincode,
        regCountry: form.regCountry || "India",
        // Communication Address
        commAddressLine1: form.commAddressLine1,
        commAddressLine2: form.commAddressLine2 || null,
        commCity: form.commCity,
        commState: form.commState,
        commPincode: form.commPincode,
        commCountry: form.commCountry || "India",
        // Bank
        bankName: form.bankName,
        accountNumber: form.accountNumber,
        branchName: form.branchName,
        ifscCode: form.ifscCode.toUpperCase(),
      };

      await API.post("/business", payload);
      toast.success("Business profile created!");
      updateUser({ accountType: "business" });
      navigate("/dashboard/business");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const steps = ["Info", "Docs", "Contacts", "Address", "Bank", "Review"];

  return (
    <div className="business-register-page">
      <div className="business-register-card">
        {/* Header */}
        <div className="business-register-header">
          {/* <img src="/logo.svg" alt="Angel Printers" style={{ height: 44, width: 'auto', marginBottom: 20, display: 'block', margin: '0 auto 20px auto' }} /> */}
          <h1>Add a Business</h1>
          <p>
            We are excited to collaborate with you. This form collects essential
            details to onboard you as a customer. Please ensure all information
            is accurate and documents are up-to-date. Fields marked with * are
            mandatory.
          </p>
        </div>

        {/* Steps Indicator */}
        <div className="steps-indicator">
          {steps.map((s, i) => (
            <div
              key={s}
              className={`step-chevron ${step > i + 1 ? "done" : ""} ${step === i + 1 ? "active" : ""}`}
            >
              <span className="step-chevron-label">
                {step > i + 1 ? (
                  <FiCheck size={11} style={{ marginRight: 4 }} />
                ) : null}
                {s}
              </span>
            </div>
          ))}
        </div>

        {/* ── Step 1: Company Info ── */}
        {step === 1 && (
          <div className="step-content">
            <h2>Company Information</h2>
            <div className="step-1-grid">
              <div className="form-group full">
                <label>
                  Company Name <span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  placeholder="Angel Printers Pvt. Ltd."
                />
              </div>
              <div className="form-group">
                <label>
                  Company Type <span className="required">*</span>
                </label>
                <select
                  className="form-control"
                  name="companyType"
                  value={form.companyType}
                  onChange={handleChange}
                >
                  <option value="">Select type</option>
                  {COMPANY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  Industry <span className="required">*</span>
                </label>
                <select
                  className="form-control"
                  name="industry"
                  value={form.industry}
                  onChange={handleChange}
                >
                  <option value="">Select industry</option>
                  {INDUSTRIES.map((i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group logo-right">
                <div
                  className="logo-upload-box"
                  onClick={() => logoInputRef.current?.click()}
                >
                  {form.logoFile?.url ? (
                    <img src={form.logoFile.url} alt="Company Logo" />
                  ) : (
                    <span className="logo-placeholder">
                      Click to upload your logo
                    </span>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg"
                  style={{ display: "none" }}
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (
                      ![
                        "image/png",
                        "image/jpeg",
                        "image/jpg",
                        "image/svg+xml",
                      ].includes(file.type)
                    ) {
                      toast.error("Only PNG, JPEG, or SVG allowed");
                      return;
                    }
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error("File must be under 5MB");
                      return;
                    }
                    const formData = new FormData();
                    formData.append("image", file);
                    try {
                      const res = await API.post("/upload/public", formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                      });
                      if (res.data.success)
                        handleFileChange("logoFile", {
                          url: res.data.url,
                          name: file.name,
                        });
                    } catch {
                      toast.error("Upload failed.");
                    }
                  }}
                />
                {form.logoFile?.url && (
                  <button
                    type="button"
                    className="file-remove-btn"
                    onClick={() => handleFileChange("logoFile", null)}
                  >
                    <FiX size={14} /> Remove
                  </button>
                )}
              </div>
              <div className="form-group full">
                <label>
                  Company Website <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  placeholder="https://yourcompany.com"
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Company Documents ── */}
        {step === 2 && (
          <div className="step-content">
            <h2>Company Documents</h2>
            <p className="step-desc">
              Upload supporting documents for verification. All fields are
              optional.
            </p>
            <div className="doc-grid">
              {/* GST */}
              <div className="doc-item">
                <div className="form-group">
                  <label>
                    GST Number <span className="optional">(optional)</span>
                  </label>
                  <input
                    className="form-control"
                    name="gstNumber"
                    value={form.gstNumber}
                    onChange={handleChange}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                    style={{ textTransform: "uppercase" }}
                  />
                </div>
                <DocUploadField
                  label="GST Certificate"
                  optional
                  fieldName="gstFile"
                  value={form.gstFile}
                  onChange={handleFileChange}
                />
              </div>
              {/* PAN */}
              <div className="doc-item">
                <div className="form-group">
                  <label>
                    PAN Number <span className="optional">(optional)</span>
                  </label>
                  <input
                    className="form-control"
                    name="panNumber"
                    value={form.panNumber}
                    onChange={handleChange}
                    placeholder="AAAAA0000A"
                    maxLength={10}
                    style={{ textTransform: "uppercase" }}
                  />
                </div>
                <DocUploadField
                  label="PAN Card"
                  optional
                  fieldName="panFile"
                  value={form.panFile}
                  onChange={handleFileChange}
                />
              </div>
              {/* TAN */}
              <div className="doc-item">
                <div className="form-group">
                  <label>
                    TAN Number <span className="optional">(optional)</span>
                  </label>
                  <input
                    className="form-control"
                    name="tanNumber"
                    value={form.tanNumber}
                    onChange={handleChange}
                    placeholder="AAAA00000A"
                    maxLength={10}
                    style={{ textTransform: "uppercase" }}
                  />
                </div>
                <DocUploadField
                  label="TAN Certificate"
                  optional
                  fieldName="tanFile"
                  value={form.tanFile}
                  onChange={handleFileChange}
                />
              </div>
              {/* Udyam */}
              <div className="doc-item">
                <div className="form-group">
                  <label>
                    Udyam Registration Number{" "}
                    <span className="optional">(optional)</span>
                  </label>
                  <input
                    className="form-control"
                    name="udyamNumber"
                    value={form.udyamNumber}
                    onChange={handleChange}
                    placeholder="UDYAM-XX-00-0000000"
                    maxLength={19}
                    style={{ textTransform: "uppercase" }}
                  />
                </div>
                <DocUploadField
                  label="Udyam Certificate"
                  optional
                  fieldName="udyamFile"
                  value={form.udyamFile}
                  onChange={handleFileChange}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 3: Contact Person ── */}
        {step === 3 && (
          <div className="step-content">
            <h2>Contact Person Details</h2>
            <p className="step-desc">Who should we contact regarding orders?</p>
            <div className="form-grid">
              <div className="form-group">
                <label>
                  Contact Person Name <span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  name="contactPersonName"
                  value={form.contactPersonName}
                  onChange={handleChange}
                  placeholder="Full Name"
                />
              </div>
              <div className="form-group">
                <label>
                  Designation <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="contactPersonDesignation"
                  value={form.contactPersonDesignation}
                  onChange={handleChange}
                  placeholder="Manager, Owner, Director..."
                />
              </div>
              <div className="form-group">
                <label>
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  name="contactPersonPhone"
                  value={form.contactPersonPhone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setForm((f) => ({ ...f, contactPersonPhone: value }));
                  }}
                  placeholder="9999999999"
                  maxLength={10}
                />
              </div>
              <div className="form-group">
                <label>
                  Email Address <span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  type="email"
                  name="contactPersonEmail"
                  value={form.contactPersonEmail}
                  onChange={handleChange}
                  placeholder="contact@company.com"
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 4: Address ── */}
        {step === 4 && (
          <div className="step-content">
            <h2>Company Address</h2>

            <div className="address-section-title">
              <span className="address-badge">Registered Address</span>
            </div>
            <div className="form-grid">
              <div className="form-group full">
                <label>
                  Address Line 1 <span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  name="regAddressLine1"
                  value={form.regAddressLine1}
                  onChange={handleChange}
                  placeholder="Building, Street, Area"
                />
              </div>
              <div className="form-group full">
                <label>
                  Address Line 2 <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="regAddressLine2"
                  value={form.regAddressLine2}
                  onChange={handleChange}
                  placeholder="Landmark, Floor, etc."
                />
              </div>
              <div className="form-group">
                <label>
                  City <span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  name="regCity"
                  value={form.regCity}
                  onChange={handleChange}
                  placeholder="Mumbai"
                />
              </div>
              <div className="form-group">
                <label>
                  State <span className="required">*</span>
                </label>
                <select
                  className="form-control"
                  name="regState"
                  value={form.regState}
                  onChange={handleChange}
                >
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  Pincode <span className="required">*</span>
                </label>
                <input
                  className="form-control"
                  name="regPincode"
                  value={form.regPincode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setForm((f) => ({ ...f, regPincode: value }));
                  }}
                  placeholder="400001"
                  maxLength={6}
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  className="form-control"
                  name="regCountry"
                  value={form.regCountry}
                  readOnly
                  style={{ background: "var(--bg-soft)" }}
                />
              </div>
            </div>

            <div className="address-section-title" style={{ marginTop: 24 }}>
              <span className="address-badge comm">Communication Address</span>
              <label className="same-address-check">
                <input
                  type="checkbox"
                  checked={sameAsReg}
                  onChange={handleSameAsReg}
                />
                Same as Registered Address
              </label>
            </div>
            <div className="form-grid">
              <div className="form-group full">
                <label>
                  Address Line 1 <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="commAddressLine1"
                  value={form.commAddressLine1}
                  onChange={handleChange}
                  placeholder="Building, Street, Area"
                  disabled={sameAsReg}
                />
              </div>
              <div className="form-group full">
                <label>
                  Address Line 2 <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="commAddressLine2"
                  value={form.commAddressLine2}
                  onChange={handleChange}
                  placeholder="Landmark, Floor, etc."
                  disabled={sameAsReg}
                />
              </div>
              <div className="form-group">
                <label>
                  City <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="commCity"
                  value={form.commCity}
                  onChange={handleChange}
                  placeholder="Mumbai"
                  disabled={sameAsReg}
                />
              </div>
              <div className="form-group">
                <label>
                  State <span className="optional">(optional)</span>
                </label>
                <select
                  className="form-control"
                  name="commState"
                  value={form.commState}
                  onChange={handleChange}
                  disabled={sameAsReg}
                >
                  <option value="">Select state</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>
                  Pincode <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="commPincode"
                  value={form.commPincode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setForm((f) => ({ ...f, commPincode: value }));
                  }}
                  placeholder="400001"
                  maxLength={6}
                  disabled={sameAsReg}
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  className="form-control"
                  name="commCountry"
                  value={form.commCountry}
                  readOnly
                  style={{ background: "var(--bg-soft)" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 5: Bank Details ── */}
        {step === 5 && (
          <div className="step-content">
            <h2>
              Bank Details{" "}
              <span
                className="optional"
                style={{ fontSize: "0.85rem", fontWeight: 400 }}
              >
                (optional)
              </span>
            </h2>
            <p className="step-desc">
              Provide your company's bank account details for payments and
              refunds.
            </p>
            <div className="form-grid">
              <div className="form-group">
                <label>
                  Bank Name <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="bankName"
                  value={form.bankName}
                  onChange={handleChange}
                  placeholder="State Bank of India"
                />
              </div>
              <div className="form-group">
                <label>
                  Account Number <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="accountNumber"
                  value={form.accountNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    setForm((f) => ({ ...f, accountNumber: value }));
                  }}
                  placeholder="00000000000"
                  maxLength={18}
                />
              </div>
              <div className="form-group">
                <label>
                  Branch Name <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="branchName"
                  value={form.branchName}
                  onChange={handleChange}
                  placeholder="Andheri West, Mumbai"
                />
              </div>
              <div className="form-group">
                <label>
                  IFSC Code <span className="optional">(optional)</span>
                </label>
                <input
                  className="form-control"
                  name="ifscCode"
                  value={form.ifscCode}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      ifscCode: e.target.value.toUpperCase(),
                    }))
                  }
                  placeholder="SBIN0001234"
                  maxLength={11}
                  style={{ textTransform: "uppercase" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 6: Review ── */}
        {step === 6 && (
          <div className="step-content">
            <h2>Review Your Details</h2>
            <p className="step-desc">
              Please confirm your business information before submitting.
            </p>

            <div className="review-section">
              <h4>🏢 Company Information</h4>
              {[
                ["Company Name", form.companyName],
                [
                  "Company Type",
                  COMPANY_TYPES.find((t) => t.value === form.companyType)
                    ?.label || form.companyType,
                ],
                ["Industry", form.industry],
                ["Website", form.website || "Not provided"],
                ["Logo", form.logoFile ? "Uploaded" : "Not provided"],
              ].map(([k, v]) => (
                <div key={k} className="review-row">
                  <span>{k}</span>
                  <strong>{v}</strong>
                </div>
              ))}
            </div>

            <div className="review-section">
              <h4>📋 Company Documents</h4>
              {[
                ["GST Number", form.gstNumber || "Not provided"],
                ["GST Document", form.gstFile ? "Uploaded" : "Not provided"],
                ["PAN Number", form.panNumber || "Not provided"],
                ["PAN Document", form.panFile ? "Uploaded" : "Not provided"],
                ["TAN Number", form.tanNumber || "Not provided"],
                ["TAN Document", form.tanFile ? "Uploaded" : "Not provided"],
                ["Udyam Number", form.udyamNumber || "Not provided"],
                [
                  "Udyam Document",
                  form.udyamFile ? "Uploaded" : "Not provided",
                ],
              ].map(([k, v]) => (
                <div key={k} className="review-row">
                  <span>{k}</span>
                  <strong>{v}</strong>
                </div>
              ))}
            </div>

            <div className="review-section">
              <h4>👤 Contact Person</h4>
              {[
                ["Name", form.contactPersonName],
                [
                  "Designation",
                  form.contactPersonDesignation || "Not provided",
                ],
                ["Phone", form.contactPersonPhone],
                ["Email", form.contactPersonEmail],
              ].map(([k, v]) => (
                <div key={k} className="review-row">
                  <span>{k}</span>
                  <strong>{v}</strong>
                </div>
              ))}
            </div>

            <div className="review-section">
              <h4>📍 Registered Address</h4>
              {[
                [
                  "Address",
                  `${form.regAddressLine1}${form.regAddressLine2 ? ", " + form.regAddressLine2 : ""}`,
                ],
                ["City", form.regCity],
                ["State", form.regState],
                ["Pincode", form.regPincode],
                ["Country", form.regCountry],
              ].map(([k, v]) => (
                <div key={k} className="review-row">
                  <span>{k}</span>
                  <strong>{v}</strong>
                </div>
              ))}
            </div>

            <div className="review-section">
              <h4>📮 Communication Address</h4>
              {sameAsReg ? (
                <div className="review-row">
                  <span>Same as Registered Address</span>
                  <strong>✓ Yes</strong>
                </div>
              ) : (
                [
                  [
                    "Address",
                    `${form.commAddressLine1}${form.commAddressLine2 ? ", " + form.commAddressLine2 : ""}`,
                  ],
                  ["City", form.commCity],
                  ["State", form.commState],
                  ["Pincode", form.commPincode],
                  ["Country", form.commCountry],
                ].map(([k, v]) => (
                  <div key={k} className="review-row">
                    <span>{k}</span>
                    <strong>{v}</strong>
                  </div>
                ))
              )}
            </div>

            <div className="review-section">
              <h4>🏦 Bank Details</h4>
              {[
                ["Bank Name", form.bankName],
                [
                  "Account Number",
                  form.accountNumber
                    ? "••••" + form.accountNumber.slice(-4)
                    : "",
                ],
                ["Branch Name", form.branchName],
                ["IFSC Code", form.ifscCode],
              ].map(([k, v]) => (
                <div key={k} className="review-row">
                  <span>{k}</span>
                  <strong>{v}</strong>
                </div>
              ))}
            </div>

            <div className="review-notice">
              ℹ️ Your business account will be reviewed and verified by our team
              within 24 hours.
            </div>

            <div className="declaration-checks">
              <label className="declaration-check">
                <input
                  type="checkbox"
                  checked={agreeCompliance}
                  onChange={(e) => setAgreeCompliance(e.target.checked)}
                />
                <span>
                  Do you agree to adhere to all statutory and regulatory
                  compliances?
                </span>
              </label>
              <label className="declaration-check">
                <input
                  type="checkbox"
                  checked={agreeDeclaration}
                  onChange={(e) => setAgreeDeclaration(e.target.checked)}
                />
                <span>
                  I hereby declare that the information provided above is true
                  and correct to the best of my knowledge.
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="step-nav">
          {step > 1 ? (
            <button
              className="btn btn-outline"
              onClick={() => setStep((s) => s - 1)}
            >
              <FiArrowLeft /> Back
            </button>
          ) : (
            <Link to="/dashboard/profile" className="btn btn-outline">
              <FiArrowLeft /> Back
            </Link>
          )}

          {step < TOTAL_STEPS ? (
            <button className="btn btn-primary" onClick={handleNext}>
              Next <FiArrowRight />
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading || !agreeCompliance || !agreeDeclaration}
            >
              {loading ? "Submitting..." : "Submit Business Profile"}
              {!loading && <FiCheck />}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessRegisterPage;
